import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
} from "recharts";
import {
  Eye, Users, MessageCircle, TrendingUp, Lock, RefreshCw, UserCheck, UserPlus,
  Activity, Clock, Globe, Smartphone, Home, BarChart3, MousePointerClick,
  ArrowUpRight, ArrowDownRight, Calendar, Zap, Search, LayoutDashboard,
  Building2, Radio, Phone, Mail, Repeat, Inbox,
} from "lucide-react";

interface Person {
  visitor_id: string;
  person_number: number | null;
  sessions: number;
  visits: number;
  pageviews: number;
  whatsapp: number;
  calls: number;
  emails: number;
  contacts: number;
  first_seen: string;
  last_seen: string;
  first_source: string;
  sources: string[];
  devices: string[];
  countries: string[];
  pages: string[];
  properties: string[];
  contact_methods: string[];
  converted: boolean;
  returning: boolean;
}

interface PropertyStat {
  id: string; titulo: string; ciudad: string; precio: number;
  views: number; unique_viewers: number;
  whatsapp: number; calls: number; emails: number; contacts: number;
  conversion: number;
}

interface AnalyticsData {
  summary: {
    uniqueVisitors: number; uniqueSessions: number; pageviews: number;
    whatsappClicks: number; callClicks: number; emailClicks: number; contactClicks: number;
    uniqueWhatsAppVisitors: number; uniqueCallVisitors: number; uniqueEmailVisitors: number; uniqueContactVisitors: number;
    conversionRate: number;
    newVisitors: number; returningVisitors: number; avgPagesPerSession: number;
    bounceRate: number; liveVisitors: number; todayVisitors: number;
    yesterdayVisitors: number; todayWhatsApp: number; todayContacts: number;
    totalProperties: number;
    excludedInternal: number; excludedLovable: number;
  };
  timeseries: any[];
  hourly: { hour: string; visits: number }[];
  dow: { day: string; visits: number }[];
  topPages: { label: string; value: number }[];
  sources: { label: string; value: number }[];
  devices: { label: string; value: number }[];
  countries: { label: string; value: number }[];
  contactsByType: { label: string; value: number }[];
  contactsBySource: { label: string; value: number }[];
  contactsByDevice: { label: string; value: number }[];
  contactsByPage: { label: string; value: number }[];
  whatsappByPage: { label: string; value: number }[];
  whatsappByDevice: { label: string; value: number }[];
  whatsappByProperty: { label: string; value: number }[];
  propertyViews: { label: string; value: number }[];
  properties: PropertyStat[];
  recentWhatsapp: any[];
  recentContacts: any[];
  recentActivity: any[];
  people: Person[];
  contactRequests?: ContactRequest[];
}

interface ContactRequest {
  id: string;
  nombre: string;
  telefono: string | null;
  email: string | null;
  mensaje: string | null;
  canal: string;
  origen: string | null;
  created_at: string;
}

const COLORS = ["#F59E0B", "#3B82F6", "#10B981", "#EF4444", "#8B5CF6", "#EC4899", "#14B8A6", "#F97316"];

type Section = "overview" | "live" | "people" | "mensajes" | "contacts" | "whatsapp" | "pages" | "traffic" | "properties";

export default function Admin() {
  const [password, setPassword] = useState("");
  const [days, setDays] = useState(30);
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [authed, setAuthed] = useState(false);
  const [section, setSection] = useState<Section>("overview");
  const [filter, setFilter] = useState<"all" | "converted" | "returning">("all");
  const [search, setSearch] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchData = async (pwd: string, d: number) => {
    setLoading(true); setError("");
    try {
      const { data: result, error: invokeError } = await supabase.functions.invoke("get-analytics", { body: { password: pwd, days: d } });
      if (invokeError) throw new Error(invokeError.message);
      if ((result as any)?.error) throw new Error((result as any).error);
      setData(result as AnalyticsData);
      setAuthed(true);
      try { sessionStorage.setItem("sercom_admin_pwd", pwd); } catch {}
    } catch (err: any) {
      setError(err.message === "unauthorized" ? "Contraseña incorrecta" : "Error al cargar los datos");
      setAuthed(false);
    } finally { setLoading(false); }
  };

  const pwd = () => password || sessionStorage.getItem("sercom_admin_pwd") || "";

  useEffect(() => {
    if (!autoRefresh || !authed) return;
    const t = setInterval(() => fetchData(pwd(), days), 30000);
    return () => clearInterval(t);
  }, [autoRefresh, authed, days, password]);

  const handleLogin = (e: React.FormEvent) => { e.preventDefault(); fetchData(password, days); };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted p-4">
        <Card className="w-full max-w-md p-8 shadow-2xl border-2">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
              <Lock className="w-5 h-5 text-accent" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Panel privado</h1>
              <p className="text-xs text-muted-foreground">Analítica de Sercom</p>
            </div>
          </div>
          <form onSubmit={handleLogin} className="space-y-4 mt-6">
            <Input type="password" placeholder="Contraseña" aria-label="Contraseña de acceso al panel" value={password}
              onChange={(e) => setPassword(e.target.value)} autoFocus />
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Comprobando..." : "Entrar"}
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  const s = data.summary;
  const visitorsDelta = s.yesterdayVisitors > 0
    ? ((s.todayVisitors - s.yesterdayVisitors) / s.yesterdayVisitors) * 100 : 0;

  const filteredPeople = data.people.filter((p) => {
    if (filter === "converted" && !p.converted) return false;
    if (filter === "returning" && !p.returning) return false;
    if (search) {
      const q = search.toLowerCase();
      return p.visitor_id.toLowerCase().includes(q)
        || p.sources.some(x => x.toLowerCase().includes(q))
        || p.pages.some(x => x.toLowerCase().includes(q))
        || p.properties.some(x => x.toLowerCase().includes(q));
    }
    return true;
  });

  const navItems: { id: Section; label: string; icon: any }[] = [
    { id: "overview", label: "Resumen", icon: LayoutDashboard },
    { id: "live", label: "En vivo", icon: Radio },
    { id: "people", label: "Personas", icon: Users },
    { id: "mensajes", label: "Mensajes", icon: Inbox },
    { id: "contacts", label: "Contactos", icon: Phone },
    { id: "whatsapp", label: "WhatsApp", icon: MessageCircle },
    { id: "pages", label: "Páginas", icon: Eye },
    { id: "traffic", label: "Tráfico", icon: Globe },
    { id: "properties", label: "Propiedades", icon: Building2 },
  ];

  return (
    <div className="min-h-screen bg-muted/30">
      <Helmet>
        <title>Panel de Administración | Sercom</title>
        <meta name="description" content="Panel privado de analítica de Sercom: visitantes únicos, contactos y rendimiento de propiedades en tiempo real." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {/* Top bar */}
      <header className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-accent" />
            </div>
            <div>
              <h1 className="text-lg font-bold leading-tight">Analítica Sercom</h1>
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                {s.liveVisitors} {s.liveVisitors === 1 ? "dispositivo activo" : "dispositivos activos"} ahora mismo
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select value={days}
              onChange={(e) => { const d = Number(e.target.value); setDays(d); fetchData(pwd(), d); }}
              className="border rounded-md px-3 py-2 bg-background text-sm">
              <option value={1}>Hoy</option>
              <option value={7}>7 días</option>
              <option value={30}>30 días</option>
              <option value={90}>90 días</option>
              <option value={365}>1 año</option>
            </select>
            <Button variant={autoRefresh ? "default" : "outline"} size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}>
              <Zap className="w-3.5 h-3.5 mr-1" />
              Auto {autoRefresh ? "ON" : "OFF"}
            </Button>
            <Button variant="outline" size="icon" aria-label="Actualizar datos" onClick={() => fetchData(pwd(), days)} disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>
        {/* Nav */}
        <nav className="max-w-[1600px] mx-auto px-4 md:px-6 flex gap-1 overflow-x-auto">
          {navItems.map((n) => {
            const Icon = n.icon;
            const active = section === n.id;
            return (
              <button key={n.id} onClick={() => setSection(n.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                  active ? "border-accent text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
                }`}>
                <Icon className="w-4 h-4" />
                {n.label}
              </button>
            );
          })}
        </nav>
      </header>

      <main className="max-w-[1600px] mx-auto p-4 md:p-6 space-y-6">
        {section === "overview" && (
          <>
            {/* Hero KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <BigKpi icon={Activity} color="green" label="Ahora mismo" value={s.liveVisitors} hint="últimos 5 min" />
              <BigKpi icon={Users} color="amber" label="Personas únicas nuevas" value={s.uniqueVisitors}
                delta={visitorsDelta} hint={`Solo primera entrada · hoy ${s.todayVisitors} · ayer ${s.yesterdayVisitors}`} />
              <BigKpi icon={Phone} color="emerald" label="Contactos totales" value={s.contactClicks}
                hint={`${s.uniqueContactVisitors} personas · hoy ${s.todayContacts}`} />
              <BigKpi icon={TrendingUp} color="blue" label="Conversión" value={`${s.conversionRate}%`}
                hint="personas que contactan" />
            </div>

            {(s.excludedInternal > 0 || s.excludedLovable > 0) && (
              <Card className="p-4 bg-muted/40 border-dashed">
                <p className="text-xs text-muted-foreground">
                  ℹ️ "Personas únicas nuevas" cuenta cada dispositivo/navegador solo el día que entra por primera vez. Se han excluido además:{" "}
                  <strong>{s.excludedInternal}</strong> de navegación interna y{" "}
                  <strong>{s.excludedLovable}</strong> del editor/preview de Lovable (eres tú probando la web).
                  Los datos que ves son visitantes reales que llegaron desde fuera.
                </p>
              </Card>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MiniKpi icon={MessageCircle} label="WhatsApp" value={s.whatsappClicks} hint={`${s.uniqueWhatsAppVisitors} personas`} />
              <MiniKpi icon={Phone} label="Llamadas" value={s.callClicks} hint={`${s.uniqueCallVisitors} personas`} />
              <MiniKpi icon={Mail} label="Emails" value={s.emailClicks} hint={`${s.uniqueEmailVisitors} personas`} />
              <MiniKpi icon={Eye} label="Páginas vistas" value={s.pageviews} />
              <MiniKpi icon={UserPlus} label="Primeras entradas" value={s.newVisitors} hint="dispositivos nuevos" />
              <MiniKpi icon={UserCheck} label="Regresos" value={s.returningVisitors} hint="ya contados antes" />
              <MiniKpi icon={Activity} label="Sesiones" value={s.uniqueSessions} />
              <MiniKpi icon={ArrowDownRight} label="Tasa de rebote" value={`${s.bounceRate}%`} />
            </div>

            <Card className="p-6">
              <SectionTitle icon={TrendingUp} title="Evolución diaria" subtitle="Personas únicas nuevas, sesiones, páginas vistas y contactos" />
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.timeseries}>
                    <defs>
                      <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.5}/>
                        <stop offset="95%" stopColor="#F59E0B" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="gp" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.4}/>
                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="date" fontSize={11} />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Legend />
                    <Area type="monotone" dataKey="visitors" stroke="#F59E0B" fill="url(#gv)" name="Personas nuevas únicas" strokeWidth={2} />
                    <Area type="monotone" dataKey="pageviews" stroke="#3B82F6" fill="url(#gp)" name="Páginas vistas" strokeWidth={2} />
                    <Line type="monotone" dataKey="whatsapp" stroke="#25D366" name="Clicks WhatsApp" strokeWidth={2.5} dot={{ r: 3 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="p-6">
                <SectionTitle icon={Clock} title="Horas con más visitas" subtitle="Distribución por hora del día" />
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.hourly}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                      <XAxis dataKey="hour" fontSize={10} />
                      <YAxis fontSize={11} />
                      <Tooltip />
                      <Bar dataKey="visits" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
              <Card className="p-6">
                <SectionTitle icon={Calendar} title="Días con más visitas" subtitle="Distribución por día de la semana" />
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.dow}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                      <XAxis dataKey="day" fontSize={11} />
                      <YAxis fontSize={11} />
                      <Tooltip />
                      <Bar dataKey="visits" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
              <ListCard icon={Eye} title="Páginas más vistas" items={data.topPages} />
              <ListCard icon={Globe} title="Fuentes de tráfico" items={data.sources} />
              <PieCard icon={Smartphone} title="Dispositivos" items={data.devices} />
            </div>
          </>
        )}

        {section === "live" && (
          <>
            <div className="grid md:grid-cols-3 gap-4">
              <BigKpi icon={Radio} color="green" label="Dispositivos ahora" value={s.liveVisitors} hint="últimos 5 minutos" />
              <BigKpi icon={Calendar} color="amber" label="Personas nuevas hoy" value={s.todayVisitors}
                delta={visitorsDelta} hint={`vs ${s.yesterdayVisitors} ayer`} />
              <BigKpi icon={MessageCircle} color="emerald" label="WhatsApp hoy" value={s.todayWhatsApp} hint="contactos del día" />
            </div>
            <Card className="p-6">
              <SectionTitle icon={Activity} title="Actividad reciente" subtitle="Últimos 50 eventos en tu web" />
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {data.recentActivity.map((e, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 border">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                      e.event_type === "whatsapp_click" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"
                    }`}>
                      {e.event_type === "whatsapp_click" ? <MessageCircle className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">
                          {e.event_type === "whatsapp_click" ? "Click WhatsApp" : "Visita a"} · {e.page}
                        </span>
                        {e.property_title && <Badge variant="secondary" className="text-xs">{e.property_title}</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Persona <span className="font-bold text-accent">#{e.person_number ?? "?"}</span> · {e.source} · {e.device}
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(e.created_at).toLocaleTimeString("es-ES")}
                    </span>
                  </div>
                ))}
                {data.recentActivity.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">Sin actividad reciente</p>
                )}
              </div>
            </Card>
          </>
        )}

        {section === "people" && (
          <Card className="p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <SectionTitle icon={Users} title={`Personas nuevas detectadas (${filteredPeople.length})`}
                subtitle="Solo aparecen dispositivos/navegadores vistos por primera vez en este periodo; los regresos no crean otra persona" />
              <div className="flex gap-2 flex-wrap">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                  <Input placeholder="Buscar..." value={search} onChange={(e) => setSearch(e.target.value)}
                    className="pl-8 h-9 w-48" />
                </div>
                <Button size="sm" variant={filter === "all" ? "default" : "outline"} onClick={() => setFilter("all")}>Todas</Button>
                <Button size="sm" variant={filter === "returning" ? "default" : "outline"} onClick={() => setFilter("returning")}>Recurrentes</Button>
                <Button size="sm" variant={filter === "converted" ? "default" : "outline"} onClick={() => setFilter("converted")}>Contactaron</Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-muted-foreground border-b bg-muted/30">
                  <tr>
                    <th className="py-2.5 px-3">Persona</th>
                    <th className="py-2.5 px-3">Primera visita</th>
                    <th className="py-2.5 px-3">Última visita</th>
                    <th className="py-2.5 px-3" title="Cuántas veces ha vuelto a entrar">Visitas</th>
                    <th className="py-2.5 px-3">Páginas</th>
                    <th className="py-2.5 px-3">Origen</th>
                    <th className="py-2.5 px-3">Dispositivo</th>
                    <th className="py-2.5 px-3">Contactos</th>
                    <th className="py-2.5 px-3">Interés</th>
                    <th className="py-2.5 px-3">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPeople.map((p) => (
                    <tr key={p.visitor_id} className="border-b last:border-0 align-top hover:bg-muted/30">
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center justify-center min-w-[2rem] h-7 px-2 rounded-full bg-accent/15 text-accent font-bold text-xs">
                            #{p.person_number ?? "?"}
                          </span>
                          <span className="font-mono text-[10px] text-muted-foreground">{p.visitor_id.slice(0, 8)}</span>
                        </div>
                      </td>
                      <td className="py-3 px-3 whitespace-nowrap text-xs">{new Date(p.first_seen).toLocaleString("es-ES")}</td>
                      <td className="py-3 px-3 whitespace-nowrap text-xs">{new Date(p.last_seen).toLocaleString("es-ES")}</td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-1 font-semibold">
                          {p.visits}
                          {p.visits > 1 && <Repeat className="w-3 h-3 text-blue-500" />}
                        </div>
                      </td>
                      <td className="py-3 px-3 font-semibold">{p.pageviews}</td>
                      <td className="py-3 px-3 text-xs">
                        <div className="font-medium">{p.first_source}</div>
                        {p.sources.length > 1 && <div className="text-muted-foreground">+{p.sources.length - 1} más</div>}
                      </td>
                      <td className="py-3 px-3 text-xs">{p.devices.join(", ") || "-"}</td>
                      <td className="py-3 px-3">
                        {p.contacts > 0 ? (
                          <div className="flex flex-col gap-0.5">
                            <span className="font-bold text-green-600">{p.contacts}</span>
                            <div className="flex gap-1 flex-wrap">
                              {p.contact_methods.map((m, i) => (
                                <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0">{m}</Badge>
                              ))}
                            </div>
                          </div>
                        ) : <span className="text-muted-foreground">-</span>}
                      </td>
                      <td className="py-3 px-3 text-xs max-w-[180px]">
                        {p.properties.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {p.properties.slice(0, 2).map((pr, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">{pr.slice(0, 25)}</Badge>
                            ))}
                            {p.properties.length > 2 && <Badge variant="outline" className="text-xs">+{p.properties.length - 2}</Badge>}
                          </div>
                        ) : <span className="text-muted-foreground">-</span>}
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex flex-col gap-1">
                          {p.converted && <Badge className="bg-green-100 text-green-800 hover:bg-green-100 w-fit">Contactó</Badge>}
                          {p.returning && <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 w-fit">Regresó</Badge>}
                          {!p.converted && !p.returning && <Badge variant="outline" className="w-fit">Nuevo</Badge>}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredPeople.length === 0 && (
                    <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">Sin personas todavía</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {section === "mensajes" && (
          <Card className="p-6">
            <SectionTitle icon={Inbox} title={`Mensajes recibidos (${(data.contactRequests || []).length})`}
              subtitle="Personas que han dejado sus datos en el formulario de contacto de la web" />
            <div className="space-y-3">
              {(data.contactRequests || []).map((r) => (
                <div key={r.id} className="rounded-xl border p-4 hover:bg-muted/30 transition-colors">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{r.nombre}</span>
                      <Badge variant="outline" className="text-xs">{r.canal}</Badge>
                    </div>
                    <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString("es-ES")}</span>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm mb-2">
                    {r.telefono && (
                      <a href={`tel:${r.telefono}`} className="flex items-center gap-1.5 text-accent hover:underline">
                        <Phone className="w-3.5 h-3.5" />{r.telefono}
                      </a>
                    )}
                    {r.telefono && (
                      <a href={`https://wa.me/${r.telefono.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-green-600 hover:underline">
                        <MessageCircle className="w-3.5 h-3.5" />WhatsApp
                      </a>
                    )}
                    {r.email && (
                      <a href={`mailto:${r.email}`} className="flex items-center gap-1.5 text-accent hover:underline">
                        <Mail className="w-3.5 h-3.5" />{r.email}
                      </a>
                    )}
                  </div>
                  {r.mensaje && <p className="text-sm text-muted-foreground whitespace-pre-line">{r.mensaje}</p>}
                  {r.origen && <p className="text-[11px] text-muted-foreground mt-2">Origen: {r.origen}</p>}
                </div>
              ))}
              {(data.contactRequests || []).length === 0 && (
                <p className="text-center text-muted-foreground py-8">Todavía no has recibido mensajes del formulario</p>
              )}
            </div>
          </Card>
        )}



        {section === "contacts" && (
          <>
            <div className="grid md:grid-cols-4 gap-4">
              <BigKpi icon={Phone} color="emerald" label="Contactos totales" value={s.contactClicks}
                hint={`${s.uniqueContactVisitors} personas distintas`} />
              <MiniKpi icon={MessageCircle} label="WhatsApp" value={s.whatsappClicks} hint={`${s.uniqueWhatsAppVisitors} personas`} />
              <MiniKpi icon={Phone} label="Llamadas" value={s.callClicks} hint={`${s.uniqueCallVisitors} personas`} />
              <MiniKpi icon={Mail} label="Emails" value={s.emailClicks} hint={`${s.uniqueEmailVisitors} personas`} />
            </div>
            <div className="grid lg:grid-cols-3 gap-6">
              <PieCard icon={MousePointerClick} title="Cómo contactan" items={data.contactsByType} />
              <BarCard icon={Eye} title="Páginas desde donde contactan" items={data.contactsByPage} color="#10B981" />
              <BarCard icon={Smartphone} title="Contactos por dispositivo" items={data.contactsByDevice} color="#3B82F6" />
            </div>
            <Card className="p-6">
              <SectionTitle icon={Globe} title="Origen de las personas que contactan"
                subtitle="De dónde venían originalmente (se cuenta su primer origen, no la sesión actual)" />
              <RankedList items={data.contactsBySource} showProgress />
            </Card>
            <Card className="p-6">
              <SectionTitle icon={Clock} title="Últimos contactos" subtitle="Todos los métodos: WhatsApp, llamada y email" />
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-muted-foreground border-b bg-muted/30">
                    <tr>
                      <th className="py-2.5 px-3">Fecha</th>
                      <th className="py-2.5 px-3">Tipo</th>
                      <th className="py-2.5 px-3">Persona</th>
                      <th className="py-2.5 px-3">Página</th>
                      <th className="py-2.5 px-3">Propiedad</th>
                      <th className="py-2.5 px-3">Origen</th>
                      <th className="py-2.5 px-3">Dispositivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recentContacts.map((r, i) => (
                      <tr key={i} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="py-2.5 px-3 whitespace-nowrap text-xs">{new Date(r.created_at).toLocaleString("es-ES")}</td>
                        <td className="py-2.5 px-3">
                          <Badge variant={r.type === "WhatsApp" ? "default" : "outline"} className="text-xs">{r.type}</Badge>
                        </td>
                        <td className="py-2.5 px-3"><span className="font-bold text-accent">#{r.person_number ?? "?"}</span></td>
                        <td className="py-2.5 px-3">{r.page}</td>
                        <td className="py-2.5 px-3 text-xs">{r.property_title || "-"}</td>
                        <td className="py-2.5 px-3 text-xs">{r.source}</td>
                        <td className="py-2.5 px-3 text-xs">{r.device}</td>
                      </tr>
                    ))}
                    {data.recentContacts.length === 0 && (
                      <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin contactos aún</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}

        {section === "whatsapp" && (
          <>
            <div className="grid md:grid-cols-3 gap-4">
              <BigKpi icon={MessageCircle} color="emerald" label="Total clicks" value={s.whatsappClicks} hint="en el periodo" />
              <BigKpi icon={Users} color="amber" label="Personas que contactaron" value={s.uniqueWhatsAppVisitors} />
              <BigKpi icon={TrendingUp} color="blue" label="Conversión" value={`${s.conversionRate}%`} hint="del total de visitantes" />
            </div>
            <div className="grid lg:grid-cols-2 gap-6">
              <BarCard icon={Eye} title="Páginas donde clican WhatsApp" items={data.whatsappByPage} color="#25D366" />
              <BarCard icon={Smartphone} title="Clicks por dispositivo" items={data.whatsappByDevice} color="#3B82F6" />
            </div>
            <Card className="p-6">
              <SectionTitle icon={Building2} title="Propiedades con más interés"
                subtitle="Clicks de WhatsApp asociados a cada propiedad (dónde clican, no de dónde vienen)" />
              <RankedList items={data.whatsappByProperty} showProgress color="#25D366" />
            </Card>
            <Card className="p-6">
              <SectionTitle icon={Clock} title="Últimos contactos por WhatsApp" />
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-muted-foreground border-b bg-muted/30">
                    <tr>
                      <th className="py-2.5 px-3">Fecha</th>
                      <th className="py-2.5 px-3">Persona</th>
                      <th className="py-2.5 px-3">Página</th>
                      <th className="py-2.5 px-3">Propiedad</th>
                      <th className="py-2.5 px-3">Dispositivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recentWhatsapp.map((r, i) => (
                      <tr key={i} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="py-2.5 px-3 whitespace-nowrap text-xs">{new Date(r.created_at).toLocaleString("es-ES")}</td>
                        <td className="py-2.5 px-3"><span className="font-bold text-accent">#{r.person_number ?? "?"}</span></td>
                        <td className="py-2.5 px-3">{r.page}</td>
                        <td className="py-2.5 px-3 text-xs">{r.property_title || "-"}</td>
                        <td className="py-2.5 px-3 text-xs">{r.device}</td>
                      </tr>
                    ))}
                    {data.recentWhatsapp.length === 0 && (
                      <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Sin contactos aún</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}

        {section === "pages" && (
          <>
            <Card className="p-6">
              <SectionTitle icon={Eye} title="Ranking de páginas" subtitle="Las páginas más visitadas en tu web" />
              <RankedList items={data.topPages} showProgress />
            </Card>
          </>
        )}

        {section === "traffic" && (
          <div className="space-y-6">
            <Card className="p-4 bg-muted/30 border-dashed">
              <p className="text-xs text-muted-foreground leading-relaxed">
                <strong>¿Qué significa cada origen?</strong><br />
                · <strong>Directo</strong>: la persona escribió la URL o vino desde un marcador / app sin referrer.<br />
                · <strong>Google, Bing, Instagram, Facebook, Idealista…</strong>: hizo click en un enlace publicado en esa plataforma.<br />
                · <strong>Navegación interna</strong>: ya estaba en sercomcs.com y navegó a otra página dentro de tu propia web (no es un visitante nuevo).<br />
                · <strong>Lovable (editor / preview)</strong>: visitas que vienen del editor de Lovable, normalmente eres tú probando la web. No son clientes reales.<br />
                Las dos últimas se excluyen del cómputo de "personas nuevas" para que los datos sean fiables.
              </p>
            </Card>
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="p-6">
                <SectionTitle icon={Globe} title="Todos los orígenes de tráfico"
                  subtitle="Cada persona contada una vez según su primer origen real" />
                <RankedList items={data.sources} showProgress />
              </Card>
              <Card className="p-6">
                <SectionTitle icon={Globe} title="Países" />
                <RankedList items={data.countries} showProgress />
              </Card>
              <PieCard icon={Smartphone} title="Reparto de dispositivos" items={data.devices} />
              <Card className="p-6">
                <SectionTitle icon={Activity} title="Comportamiento" />
                <div className="space-y-3">
                  <Stat label="Páginas vistas / sesión" value={s.avgPagesPerSession} />
                  <Stat label="Tasa de rebote" value={`${s.bounceRate}%`} hint="sesiones con 1 sola página" />
                  <Stat label="% nuevos visitantes"
                    value={s.uniqueVisitors > 0 ? `${Math.round((s.newVisitors / s.uniqueVisitors) * 100)}%` : "0%"} />
                  <Stat label="% recurrentes"
                    value={s.uniqueVisitors > 0 ? `${Math.round((s.returningVisitors / s.uniqueVisitors) * 100)}%` : "0%"} />
                </div>
              </Card>
            </div>
          </div>
        )}

        {section === "properties" && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MiniKpi icon={Building2} label="Propiedades" value={s.totalProperties} />
              <MiniKpi icon={Eye} label="Con visitas" value={data.properties.filter(p => p.views > 0).length} />
              <MiniKpi icon={Phone} label="Con contactos" value={data.properties.filter(p => p.contacts > 0).length} />
              <MiniKpi icon={TrendingUp} label="Mejor conversión"
                value={`${data.properties.length ? Math.max(...data.properties.map(p => p.conversion)) : 0}%`} />
            </div>
            <Card className="p-6">
              <SectionTitle icon={Building2} title="Detalle por propiedad" subtitle="Todas las propiedades publicadas con su rendimiento" />
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-muted-foreground border-b bg-muted/30">
                    <tr>
                      <th className="py-2.5 px-3">Propiedad</th>
                      <th className="py-2.5 px-3">Ciudad</th>
                      <th className="py-2.5 px-3 text-right">Vistas</th>
                      <th className="py-2.5 px-3 text-right">Personas</th>
                      <th className="py-2.5 px-3 text-right">WhatsApp</th>
                      <th className="py-2.5 px-3 text-right">Llamadas</th>
                      <th className="py-2.5 px-3 text-right">Emails</th>
                      <th className="py-2.5 px-3 text-right">Contactos</th>
                      <th className="py-2.5 px-3 text-right">Conv.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.properties.map((p) => (
                      <tr key={p.id} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="py-2.5 px-3 font-medium max-w-[260px] truncate" title={p.titulo}>{p.titulo}</td>
                        <td className="py-2.5 px-3 text-xs text-muted-foreground">{p.ciudad || "-"}</td>
                        <td className="py-2.5 px-3 text-right">{p.views}</td>
                        <td className="py-2.5 px-3 text-right">{p.unique_viewers}</td>
                        <td className="py-2.5 px-3 text-right text-green-600 font-semibold">{p.whatsapp}</td>
                        <td className="py-2.5 px-3 text-right">{p.calls}</td>
                        <td className="py-2.5 px-3 text-right">{p.emails}</td>
                        <td className="py-2.5 px-3 text-right font-bold">{p.contacts}</td>
                        <td className="py-2.5 px-3 text-right">
                          <Badge variant={p.conversion > 5 ? "default" : "outline"}>{p.conversion}%</Badge>
                        </td>
                      </tr>
                    ))}
                    {data.properties.length === 0 && (
                      <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">Sin propiedades publicadas</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-6">
                <SectionTitle icon={Eye} title="Top vistas" />
                <RankedList items={data.propertyViews} showProgress />
              </Card>
              <Card className="p-6">
                <SectionTitle icon={MessageCircle} title="Top contactos WhatsApp" />
                <RankedList items={data.whatsappByProperty} showProgress color="#25D366" />
              </Card>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function BigKpi({ icon: Icon, color, label, value, hint, delta }: any) {
  const colorMap: Record<string, string> = {
    green: "from-green-500/20 to-green-500/5 text-green-600",
    amber: "from-amber-500/20 to-amber-500/5 text-amber-600",
    emerald: "from-emerald-500/20 to-emerald-500/5 text-emerald-600",
    blue: "from-blue-500/20 to-blue-500/5 text-blue-600",
  };
  return (
    <Card className={`p-5 bg-gradient-to-br ${colorMap[color]} border`}>
      <div className="flex items-center justify-between mb-3">
        <Icon className="w-5 h-5" />
        {delta !== undefined && delta !== 0 && (
          <span className={`text-xs font-semibold flex items-center gap-0.5 ${delta > 0 ? "text-green-600" : "text-red-600"}`}>
            {delta > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {Math.abs(delta).toFixed(0)}%
          </span>
        )}
      </div>
      <div className="text-3xl font-bold text-foreground">{value}</div>
      <div className="text-sm font-medium text-foreground/80 mt-1">{label}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </Card>
  );
}

function MiniKpi({ icon: Icon, label, value, hint }: any) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 text-muted-foreground mb-1">
        <Icon className="w-4 h-4" />
        <span className="text-xs">{label}</span>
      </div>
      <div className="text-xl font-bold">{value}</div>
      {hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}
    </Card>
  );
}

function SectionTitle({ icon: Icon, title, subtitle }: any) {
  return (
    <div className="mb-4">
      <h2 className="font-semibold flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-accent" />}
        {title}
      </h2>
      {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
    </div>
  );
}

function ListCard({ icon, title, items }: any) {
  return (
    <Card className="p-6">
      <SectionTitle icon={icon} title={title} />
      <RankedList items={items} showProgress />
    </Card>
  );
}

function RankedList({ items, showProgress, color = "#F59E0B" }: { items: { label: string; value: number }[]; showProgress?: boolean; color?: string }) {
  if (!items || items.length === 0) return <p className="text-sm text-muted-foreground">Sin datos</p>;
  const max = Math.max(...items.map((i) => i.value), 1);
  const total = items.reduce((a, b) => a + b.value, 0);
  return (
    <ul className="space-y-2.5">
      {items.map((item, i) => (
        <li key={i} className="text-sm">
          <div className="flex justify-between items-center mb-1 gap-2">
            <span className="truncate flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-5">#{i + 1}</span>
              {item.label}
            </span>
            <span className="font-semibold whitespace-nowrap">
              {item.value} <span className="text-xs text-muted-foreground font-normal">({total > 0 ? Math.round((item.value / total) * 100) : 0}%)</span>
            </span>
          </div>
          {showProgress && (
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${(item.value / max) * 100}%`, backgroundColor: color }} />
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

function BarCard({ icon, title, items, color = "#F59E0B" }: any) {
  return (
    <Card className="p-6">
      <SectionTitle icon={icon} title={title} />
      {items.length === 0 ? <p className="text-sm text-muted-foreground">Sin datos</p> : (
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={items}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="label" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip />
              <Bar dataKey="value" fill={color} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

function PieCard({ icon, title, items }: any) {
  return (
    <Card className="p-6">
      <SectionTitle icon={icon} title={title} />
      {items.length === 0 ? <p className="text-sm text-muted-foreground">Sin datos</p> : (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={items} dataKey="value" nameKey="label" cx="50%" cy="50%" outerRadius={80} label>
                {items.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

function Stat({ label, value, hint }: any) {
  return (
    <div className="flex items-baseline justify-between border-b last:border-0 pb-2 last:pb-0">
      <div>
        <div className="text-sm font-medium">{label}</div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </div>
      <div className="text-xl font-bold">{value}</div>
    </div>
  );
}
